import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import BusinessPartnerSummaryService, { ContactsSummary } from '../services/BusinessPartnerSummaryService';
import ContactsDashboard from './ContactsDashboard';
import ContactsListTab from './ContactsListTab';
import ContactModal from './ContactModal';
import { BusinessPartner, LifecycleStage } from '../services/BusinessPartnerService';
import PrimaryButton from '../../../components/PrimaryButton';

type ContactsTabKey = 'overview' | 'leads' | 'customers' | 'partners' | 'suppliers' | 'all';

const TABS: { key: ContactsTabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'leads', label: 'Leads' },
  { key: 'customers', label: 'Customers' },
  { key: 'partners', label: 'Partners' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'all', label: 'All Contacts' },
];

export default function ContactsPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<ContactsSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const urlTab = searchParams.get('tab') as ContactsTabKey;
  const activeTab: ContactsTabKey = TABS.some((t) => t.key === urlTab) ? urlTab : 'overview';

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = () => {
    setLoadingSummary(true);
    BusinessPartnerSummaryService.getSummary()
      .then(setSummary)
      .catch((err) => {
        console.error('Failed to load contacts summary:', err);
      })
      .finally(() => setLoadingSummary(false));
  };

  const handleTabChange = (tab: ContactsTabKey) => {
    setSearchParams({ tab });
  };

  const handleDashboardNavigate = (kind: 'CUSTOMER' | 'PARTNER' | 'SUPPLIER' | null) => {
    if (kind === 'CUSTOMER') {
      setSearchParams({ tab: 'customers' });
    } else if (kind === 'PARTNER') {
      setSearchParams({ tab: 'partners' });
    } else if (kind === 'SUPPLIER') {
      setSearchParams({ tab: 'suppliers' });
    } else {
      setSearchParams({ tab: 'all' });
    }
  };

  const handleStageFilterChange = (stage: 'leads' | 'customers') => {
    setSearchParams({ tab: stage });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    if (searchParams.get('addContact')) {
      searchParams.delete('addContact');
      setSearchParams(searchParams);
    }
  };

  const handleModalSave = (_newContact: BusinessPartner) => {
    setModalOpen(false);
    if (searchParams.get('addContact')) {
      searchParams.delete('addContact');
      setSearchParams(searchParams);
    }
    loadSummary();
  };

  // Open modal via query param (from QuickAddButton)
  useEffect(() => {
    if (searchParams.get('addContact') === 'true') {
      setModalOpen(true);
    }
  }, [searchParams]);

  const getKindForTab = (tab: ContactsTabKey): 'CUSTOMER' | 'PARTNER' | 'SUPPLIER' | undefined => {
    switch (tab) {
      case 'leads':
      case 'customers':
        return 'CUSTOMER';
      case 'partners':
        return 'PARTNER';
      case 'suppliers':
        return 'SUPPLIER';
      default:
        return undefined;
    }
  };

  const getLifecycleStagesForTab = (tab: ContactsTabKey): LifecycleStage[] | undefined => {
    switch (tab) {
      case 'leads':
        return ['LEAD', 'PROSPECT'];
      case 'customers':
        return ['ACTIVE', 'INACTIVE'];
      default:
        return undefined;
    }
  };

  const getTitleForTab = (tab: ContactsTabKey): string | undefined => {
    switch (tab) {
      case 'leads':
        return 'Leads & Prospects';
      case 'customers':
        return 'Customers';
      default:
        return undefined;
    }
  };

  return (
    <div style={{ padding: '16px', position: 'relative', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.2)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? colors.slate : colors.textMuted,
                    borderRadius: 9999,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = colors.slate;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.textMuted;
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <PrimaryButton onClick={() => setModalOpen(true)} icon={<FaPlus size={14} />}>
            New Contact
          </PrimaryButton>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {loadingSummary ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>Loading...</div>
          ) : summary ? (
            <ContactsDashboard
              summary={summary}
              onFilterChange={handleDashboardNavigate}
              onStageFilterChange={handleStageFilterChange}
            />
          ) : (
            <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
              Failed to load dashboard
            </div>
          )}
        </>
      )}

      {activeTab !== 'overview' && (
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            minHeight: '400px',
          }}
        >
          <ContactsListTab
            kind={getKindForTab(activeTab)}
            lifecycleStages={getLifecycleStagesForTab(activeTab)}
            title={getTitleForTab(activeTab)}
            onContactCreated={loadSummary}
          />
        </div>
      )}

      {/* Modal */}
      <ContactModal isOpen={modalOpen} contact={null} onClose={handleModalClose} onSave={handleModalSave} />
    </div>
  );
}
